"use client";
import { TeamsProps } from "@/types/sections/teams";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import { Globe } from "lucide-react";

export default function TeamsCardsSection({ data }: { data: TeamsProps }) {
  const {
    title,
    description,
    members,
    layout = "grid",
    columns = 3,
    background,
  } = data;

  const backgroundStyle =
    background?.type === "color"
      ? { backgroundColor: background.value }
      : background?.type === "gradient"
        ? { backgroundImage: background.value }
        : background?.type === "image"
          ? {
              backgroundImage: `url(${background.value})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {};

  return (
    <section style={backgroundStyle} className="py-16 px-4">
      <div className="max-w-7xl mx-auto space-y-8 text-center">
        {title && <h2 className="text-3xl font-bold">{title}</h2>}
        {description && <p className="text-muted-foreground">{description}</p>}

        <div
          className={`grid gap-8 mt-10 grid-cols-1 sm:grid-cols-2 md:grid-cols-${columns}`}
        >
          {members.map((member, i) => (
            <Card key={i} className="text-center p-6 space-y-4 shadow-md">
              {member.avatar && (
                <Image
                  src={member.avatar}
                  alt={member.name}
                  width={100}
                  height={100}
                  className="mx-auto rounded-full object-cover aspect-square"
                />
              )}
              <CardContent className="space-y-2">
                <p className="text-lg font-semibold">{member.name}</p>
                {member.role && (
                  <p className="text-sm text-muted-foreground">{member.role}</p>
                )}
                {member.bio && <p className="text-sm">{member.bio}</p>}

                {Array.isArray(member.socials) &&
                  member.socials?.length > 0 && (
                    <div className="flex justify-center gap-3 pt-2">
                      {member.socials.map((s, idx) => (
                        <a
                          key={idx}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-sm hover:underline flex items-center gap-1"
                        >
                          <Globe size={16} />
                          {s.platform}
                        </a>
                      ))}
                    </div>
                  )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
